import React, { useRef, useState } from "react";
import { Dialog } from "../../packages/excalidraw/components/Dialog";
import type { ExcalidrawImperativeAPI } from "../../packages/excalidraw/types";
import { createFile, softDeleteFile } from "../services/filesService";
import {
  saveSceneToCloud,
  CLOUD_FILE_INITIAL_VERSION,
} from "../data/cloudScene";
import { generateAndUploadThumbnail } from "../data/thumbnail";
import { fireNudgeTrigger } from "../components/canvas/feedback/feedbackNudgeLogic";

interface Props {
  excalidrawAPI: ExcalidrawImperativeAPI;
  accessToken?: string;
  onClose: () => void;
  /** Called with the new file id after the row + first scene PUT succeed. */
  onSaved: (fileId: string) => void;
}

/**
 * Minimal "Save to My Files" dialog: name the file, create its metadata row,
 * push the first scene to the cloud, then hand the new id back to the parent
 * (which navigates to `/f/<id>`).
 */
const SaveToCloudDialog: React.FC<Props> = ({
  excalidrawAPI,
  accessToken,
  onClose,
  onSaved,
}) => {
  const [title, setTitle] = useState("Untitled");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Minted once per dialog-open session and reused across retries, so a
  // retry after a failed attempt updates the same row (createFile upserts)
  // instead of leaving the old one orphaned and minting a sibling. Reset on
  // success or close so the NEXT time this dialog opens gets a fresh id.
  const idRef = useRef<string | null>(null);

  const handleSave = async () => {
    if (!accessToken) {
      setError("You must be signed in to save to My Files.");
      return;
    }
    const elements = excalidrawAPI.getSceneElements();
    if (elements.length === 0) {
      setError("Cannot save an empty canvas.");
      return;
    }

    setSaving(true);
    setError("");
    const id = idRef.current ?? (idRef.current = crypto.randomUUID());
    // Tracks whether createFile completed, so a failure before the row ever
    // existed doesn't try to roll back a row that was never created.
    let rowCreated = false;
    try {
      await createFile({ id, title: title.trim() || "Untitled" });
      rowCreated = true;
      const { version, errorMessage } = await saveSceneToCloud(
        id,
        elements,
        excalidrawAPI.getAppState(),
        excalidrawAPI.getFiles(),
        accessToken,
        // The row was created a line ago, so nothing can have committed on top.
        CLOUD_FILE_INITIAL_VERSION,
      );
      // saveSceneToCloud reports failure by return value, not by throwing — and
      // a 2xx with a missing `data.version` still comes back as
      // `version === null`. Treating that as success would leave an orphan row
      // (the metadata exists but the scene never landed), matching the guard in
      // filesService#duplicateFile.
      if (errorMessage || version === null) {
        throw new Error(errorMessage || "Could not save. Please try again.");
      }
      // Fire-and-forget: give the new file a thumbnail immediately (autosave is
      // the only other path that generates one, so a dialog-saved file would
      // otherwise show a placeholder in My Files until its next edit).
      // `generateAndUploadThumbnail` never rejects and swallows its own errors.
      void generateAndUploadThumbnail(
        id,
        excalidrawAPI.getSceneElements(),
        excalidrawAPI.getAppState(),
        excalidrawAPI.getFiles(),
        accessToken,
      );
      idRef.current = null;
      onSaved(id);
      // The row exists and the scene landed, so this is a moment worth
      // asking on.
      fireNudgeTrigger();
    } catch (err: any) {
      // Roll the row back so a failed attempt never leaves an orphan in My
      // Files. Only when the row actually exists — a failure inside
      // createFile itself means there's nothing to delete.
      if (rowCreated) {
        try {
          await softDeleteFile(id);
        } catch {
          // Rollback failed too; nothing more to do client-side.
        }
      }
      setError(err?.message || "Could not save. Please try again.");
      setSaving(false);
    }
  };

  const handleClose = () => {
    idRef.current = null;
    onClose();
  };

  return (
    <Dialog size="small" onCloseRequest={handleClose} title="Save to My Files">
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {error && (
          <p
            role="alert"
            style={{
              color: "#b91c1c",
              background: "#fee2e2",
              borderRadius: "0.375rem",
              padding: "0.5rem 0.75rem",
              fontSize: "0.85rem",
              margin: 0,
            }}
          >
            {error}
          </p>
        )}
        <input
          autoFocus
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          placeholder="Enter a title for your mindmap"
          aria-label="File title"
          style={{
            border: "1.5px solid #9ca3af",
            borderRadius: "0.375rem",
            width: "100%",
            padding: "0.75rem 1rem",
            fontSize: "1rem",
          }}
        />
        <button
          onClick={handleSave}
          disabled={saving || !title.trim()}
          style={{
            background: "#6965db",
            color: "#fff",
            padding: "0.75rem 1.25rem",
            borderRadius: "0.375rem",
            fontSize: "0.9rem",
            border: "none",
            cursor: saving ? "default" : "pointer",
            opacity: saving || !title.trim() ? 0.6 : 1,
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </Dialog>
  );
};

export default SaveToCloudDialog;
